import { EnumSettingApply } from '@/api/com/MetaPattern/APP/MDA/User/EnumSettingApply';
import { Product } from '@/api/com/MetaPattern/MDA/MD/Base/Product';
import { MDAUserService } from '../../../../../api/com/MetaPattern/APP/MDA/User/MDAUserService';
import { SelectGroup } from '../../../../../api/com/MetaPattern/APP/MDA/User/SelectGroup';
import { SettingGroup } from '../../../../../api/com/MetaPattern/APP/MDA/User/SettingGroup';
import { ActionType } from '../../../../../api/com/MetaPattern/MDA/MD/UIModel/ActionType';
import { ActionTypeService } from '../../../../../api/com/MetaPattern/MDA/MD/UIModel/ActionTypeService';
import { IDEContext } from './IDEContext';
import { EnumStorageType, StorageLib } from './Util/StorageLib';
import { TreeNode } from './Util/TreeNode';

// IDE应用程序：完成IDE的一系列启动工作
// 1、配置全局参数
// 2、获取全局配置
// 3、构建上下文
export class IDEProgram {
  private static _instance = new IDEProgram();
  private actionTypes: Array<ActionType> = null;
  private StorageKeys = {
    SETTING_KEY: 'IDESetting',
    SELECT_KEY: 'IDESetting_SelectGroup'
  };

  // 获取唯一实例
  public static get instance() {
    return this._instance;
  }

  /** 获取UIModel定义的所有的操作类型 */
  public getActionTypes() {
    return this.actionTypes;
  }

  /** 获取基础设施应用的APPKEY */
  public get InfrastructureAPPKey() {
    return '10001';
  }

  /** 获取基础设施应用的BaseEntity的设计器ID */
  public get BaseEntityDesignId() {
    return 'bc5939d5-45e9-4874-99e2-d7459f294ece';
  }

  /** 获取基础设施应用的AuditEntity的设计器ID */
  public get AuditEntityDesignId() {
    return '0f62a56f-ac1b-45b9-8a28-2b22d0f23d2b';
  }

  // 初始化
  // 1. 构建IDE的整体信息
  // 1.1 构建IDE的设置信息
  // 2. 构建用户的整体信息
  // 2.1 构建上下文环境
  public async initAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      const buildSettingPromise = IDEProgram.instance.buildSettingAsync();
      const contextInitPromise = IDEContext.instance.initAsync();
      const actionTypePromise = IDEProgram.instance.buildActionTypesAsync();
      Promise.all([buildSettingPromise, contextInitPromise, actionTypePromise])
        .then(() => {
          resolve();
        })
        .catch(error => {
          // TODO : 格式化错误返回
          reject(error);
        });
    });
  }

  /**
   * 获取全局设置
   * @param settingApply 设置应用，使用枚举EnumSettingApply
   * @param product 产品实体，产品设置的需要提供
   * @returns 设置树
   */
  public getSetting(settingApply: number, product: Product = null) {
    const treeData = new Array<TreeNode<SettingGroup>>();
    this.getSettingInner(settingApply, treeData, '', '');
    // 产品下的应用配置
    if ((settingApply & EnumSettingApply.Product) > 0 && product !== null && product.productComponents[0].productAPPs.length > 0) {
      product.productComponents[0].productAPPs.forEach(productAPP => {
        this.getSettingInner(EnumSettingApply.ProductAPP, treeData, productAPP.aPP_ID, `${productAPP.displayName}(${productAPP.name})设置`);
      });
    }
    return treeData;
  }

  private getSettingInner(settingApply: number, treeData: Array<TreeNode<SettingGroup>>, treeNodeId: string, treeNodeLabel: string) {
    const treeNodes = StorageLib.instance.getItem<Array<TreeNode<SettingGroup>>>(EnumStorageType.Persistent, this.StorageKeys.SETTING_KEY);
    treeNodes.forEach((treeNode: any) => {
      const nodeData = new TreeNode<SettingGroup>(treeNode.id, treeNode.label, treeNode.id, treeNode.isLeaf, new SettingGroup());
      nodeData.fromJson(treeNode);
      nodeData.data.settingItems = nodeData.data.settingItems.filter(item => (item.settingApply & settingApply) > 0);
      let itemCount = nodeData.data.settingItems.length;
      if (treeNode.children) {
        treeNode.children.forEach((child: any) => {
          const childNode = new TreeNode<SettingGroup>(child.id, child.label, child.id, child.isLeaf, new SettingGroup());
          childNode.fromJson(child);
          if (childNode.data.settingItems.length > 0) {
            childNode.data.settingItems = childNode.data.settingItems.filter(item => (item.settingApply & settingApply) > 0);
            itemCount += childNode.data.settingItems.length;
            if (childNode.data.settingItems.length > 0) {
              nodeData.children.push(childNode);
            }
          }
        });
      }
      if (itemCount > 0) {
        if (treeNodeId !== '') {
          let findNode = treeData.find(item => item.id === treeNodeId);
          if (!findNode) {
            findNode = new TreeNode<SettingGroup>(treeNodeId, treeNodeLabel, treeNodeId, false, new SettingGroup());
            treeData.push(findNode);
          }
          if (!findNode.children.find(child => child.id === nodeData.id)) {
            findNode.children.push(nodeData);
          }
        } else {
          treeData.push(nodeData);
        }
      }
    });
  }

  // 获取选项组
  public getSelectGroups() {
    const selectGroups = new Array<SelectGroup>();
    const selectGroupDatas = StorageLib.instance.getItem<Array<SelectGroup>>(EnumStorageType.Persistent, this.StorageKeys.SELECT_KEY);
    selectGroupDatas.forEach(selectGroupData => {
      const selectGroup = new SelectGroup();
      selectGroup.fromJson(selectGroupData);
      selectGroups.push(selectGroup);
    });
    return selectGroups;
  }

  // 获取IDE设置，存储于Storage中，
  private async buildSettingAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      const settings = StorageLib.instance.getItem<Array<TreeNode<SettingGroup>>>(EnumStorageType.Persistent, this.StorageKeys.SETTING_KEY);
      if (settings === null) {
        const settingGroupPromise = MDAUserService.GetSettingGroupsAsync();
        const selectGroupPromise = MDAUserService.GetSelectGroupsAsync();
        Promise.all([settingGroupPromise, selectGroupPromise])
          .then((results: [Array<SettingGroup>, Array<SelectGroup>]) => {
            const trees = new Array<TreeNode<SettingGroup>>();
            const settingGroups = results[0];
            const selectGroups = results[1];
            const selectGroupDatas = new Array<SelectGroup>();
            selectGroups.forEach(selectGroup => {
              selectGroupDatas.push(selectGroup.toJson());
            });
            settingGroups.forEach(settingGroup => {
              const parentNode = new TreeNode(settingGroup.id, settingGroup.name, settingGroup.id, settingGroup.children.length === 0, settingGroup);
              settingGroup.children.forEach(child => {
                const childNode = new TreeNode(child.id, child.name, child.id, true, child);
                parentNode.children.push(childNode);
              });
              trees.push(parentNode.toJson());
            });
            StorageLib.instance.setItem(EnumStorageType.Persistent, this.StorageKeys.SETTING_KEY, trees);
            StorageLib.instance.setItem(EnumStorageType.Persistent, this.StorageKeys.SELECT_KEY, selectGroupDatas);
            resolve();
          })
          .catch(error => {
            // TODO : 格式化错误返回
            reject(error);
          });
      } else {
        resolve();
      }
    });
  }

  /** 获取UI模型组件的操作类型 */
  private async buildActionTypesAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      ActionTypeService.LoadAllAsync()
        .then((actionTypes: Array<ActionType>) => {
          this.actionTypes = actionTypes;
          resolve();
        })
        .catch(error => {
          // TODO : 格式化错误返回
          reject(error);
        });
    });
  }
}
